// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import {
  TestableCard,
  TestableText,
  TestableTable,
  TestableTableHead,
  TestableTableRow,
  TestableTableHeaderCell,
  TestableTableBody,
  TestableTableCell,
} from '@components/general/TestableComponents';
import { convertDateToLocal, featureDetails } from '@datahooks/metadata/DataUtils';

import { getResultMeta } from '../shared/businessViewConfig';
import PassFailChip from '@components/cards/shared/PassFailChip';

/**
 * Test type badge, using the same icon the rest of the app uses for that test
 * type so a type is recognisable across pages.
 * @param {{ testTypeId: number, testType: string, dataTestId?: string }} props
 */
const TestTypeBadge = ({ testTypeId, testType, dataTestId }) => {
  const Icon = featureDetails[testTypeId]?.icon;

  return (
    <span
      data-testid={dataTestId}
      className="inline-flex items-start gap-1.5 rounded-md border border-gray-400 px-2 py-0.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
    >
      {/* The icon keeps its own size: only the label wraps, so a long type name cannot widen the column. */}
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
      <span className="break-words">{featureDetails[testTypeId]?.title || testType}</span>
    </span>
  );
};

TestTypeBadge.propTypes = {
  testTypeId: PropTypes.number,
  testType: PropTypes.string,
  dataTestId: PropTypes.string,
};

/**
 * The linked tests behind a flow's status. Every result the calculation can store is
 * rendered, not just passed and failed: a skipped or not-found test is why a flow's
 * numbers do not add up, so it says so rather than being shown as a failure.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').FlowLinkedTestRow[]} props.tests
 * @param {number} props.passingCount
 * @param {number} props.nonPassingCount
 * @returns {JSX.Element}
 */
const BusinessViewLinkedTestsTable = ({ tests = [], passingCount, nonPassingCount }) => (
  <div data-testid="bv-linked-tests" className="flex flex-col gap-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <TestableText dataTestId="bv-linked-tests-title" variant="h3" className="normal-case">
          Linked Tests
        </TestableText>
        <span
          data-testid="bv-linked-tests-count"
          className="inline-flex items-center rounded-full border border-gray-300 px-2 py-0.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
        >
          {tests.length}
        </span>
      </div>

      {tests.length > 0 && (
        <TestableText dataTestId="bv-linked-tests-summary" className="text-gray-700 dark:text-gray-300">
          {nonPassingCount} non-passing | {passingCount} passing
        </TestableText>
      )}
    </div>

    <TestableCard dataTestId="bv-linked-tests-card" className="p-5 shadow-sm">
      {/*
        Fixed layout with declared widths so cells wrap in their column: a generated test
        name or AI headline would otherwise widen the table past the card into a scrollbar.
        Percentages cover the visible columns only, so they exceed 100% once the md-only
        ones are hidden - the browser scales them back.

        A style rather than a class because Tremor puts className on the scroll container
        that wraps the table, not on the <table> itself.
      */}
      <TestableTable dataTestId="bv-linked-tests-table" style={{ tableLayout: 'fixed' }}>
        <TestableTableHead dataTestId="bv-linked-tests-table-head" className="grey-title-border">
          <TestableTableRow dataTestId="bv-linked-tests-table-head-row">
            {/* Test IDs are short today but expected to grow, so the column has room and breaks. */}
            <TestableTableHeaderCell dataTestId="bv-linked-tests-table-head-cell-test-id" className="w-[14%] whitespace-normal break-words px-3 uppercase text-blue-500">Test ID</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="bv-linked-tests-table-head-cell-name" className="w-[32%] whitespace-normal px-3 uppercase text-blue-500">Test Name / Description</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="bv-linked-tests-table-head-cell-type" className="w-[16%] whitespace-normal px-3 uppercase text-blue-500">Type</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="bv-linked-tests-table-head-cell-affected-area" className="hidden w-[14%] whitespace-normal px-3 uppercase text-blue-500 md:table-cell">Affected Area</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="bv-linked-tests-table-head-cell-last-run" className="hidden w-[14%] whitespace-normal px-3 uppercase text-blue-500 md:table-cell">Last Run</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="bv-linked-tests-table-head-cell-result" className="w-[10%] whitespace-normal px-3 uppercase text-right text-blue-500">Result</TestableTableHeaderCell>
          </TestableTableRow>
        </TestableTableHead>
        <TestableTableBody dataTestId="bv-linked-tests-table-body">
          {tests.map((test) => {
            const { label, textClass, borderClass, isPassing } = getResultMeta(test.result);
            const accentClass = isPassing ? '' : `border-l-2 ${borderClass}`;

            return (
              <TestableTableRow
                key={test.key}
                dataTestId={`bv-linked-test-row-${test.key}`}
                /*
                  grey-title-border is !important because Tremor's TableBody carries
                  `divide-y divide-tremor-border`, whose `.divide-y > * ~ *` selector outranks a
                  plain utility class and would otherwise colour every separator itself - leaving
                  the first row's rule invisible and the rest too dark.

                  Safe alongside the row accent because that accent sits on the first cell:
                  border-color set here does not inherit into a <td>.
                */
                className="last:border-0 grey-title-border"
              >
                {/*
                  Every non-passing result is flagged down the edge of the row, not only a
                  genuine failure: a skipped or missing test counts against the flow's status
                  just as a failure does. The colour comes from the result's own borderClass so
                  the bar always matches its Result label - a failure's pink, a no-verdict
                  result's orange - and the bar never claims a skipped test failed.

                  break-all rather than break-words: a test ID is a single unbroken token with
                  nowhere to wrap, so it has to be free to break anywhere.
                */}
                <TestableTableCell
                  dataTestId={`bv-linked-test-cell-id-${test.key}`}
                  className={`align-top whitespace-normal break-all px-3 ${accentClass}`}
                >
                  {test.sourceTestId || <span className="text-gray-500">&mdash;</span>}
                </TestableTableCell>

                <TestableTableCell dataTestId={`bv-linked-test-cell-name-${test.key}`} className="align-top whitespace-normal break-words px-3">
                  <div className="font-medium" title={test.nameWithFnArea}>
                    {test.name}
                  </div>
                  {/*
                    A failure explains itself (AI headline where one exists, otherwise the
                    source error); every other non-passing result explains why it produced
                    no verdict, which is not a failure and is not shown as one.
                  */}
                  {test.description && (
                    <div
                      data-testid={`bv-linked-test-description-${test.key}`}
                      className={`mt-1 text-sm font-bold ${textClass} flex items-center`}
                      title={test.failureDescriptionSource === 'ai' ? 'Error summarised by AI analysis' : undefined}
                    >											
                      {test.description}
                    </div>
                  )}
                </TestableTableCell>

                <TestableTableCell dataTestId={`bv-linked-test-cell-type-${test.key}`} className="align-top whitespace-normal px-3">
                  <TestTypeBadge
                    dataTestId={`bv-linked-test-type-${test.key}`}
                    testTypeId={test.testTypeId}
                    testType={test.testType}
                  />
                </TestableTableCell>

                <TestableTableCell dataTestId={`bv-linked-test-cell-affected-area-${test.key}`} className="hidden align-top whitespace-normal break-words px-3 md:table-cell">
                  {test.affectedArea || <span className="text-gray-500">&mdash;</span>}
                </TestableTableCell>

                <TestableTableCell dataTestId={`bv-linked-test-cell-last-run-${test.key}`} className="hidden align-top whitespace-normal px-3 md:table-cell">
                  {/* A test that was never found in the results has no run to report. */}
                  {test.runAt ? (
                    convertDateToLocal(test.runAt, true, true, false)
                  ) : (
                    <span className="text-gray-500">&mdash;</span>
                  )}
                </TestableTableCell>

                {/* The cell carries the alignment; the chip carries the result and its colour. */}
                <TestableTableCell dataTestId={`bv-linked-test-cell-result-${test.key}`} className="align-top whitespace-normal px-3 text-right">
                  <PassFailChip dataTestId={`bv-linked-test-result-${test.key}`} result={test.result} label={label} className="ml-auto w-fit" />
                </TestableTableCell>
              </TestableTableRow>
            );
          })}

          {tests.length === 0 && (
            <TestableTableRow dataTestId="bv-linked-tests-table-empty-row">
              <TestableTableCell dataTestId="bv-linked-tests-table-empty-cell" colSpan={6} className="text-center text-gray-500">
                No tests are linked to this flow for this run.
              </TestableTableCell>
            </TestableTableRow>
          )}
        </TestableTableBody>
      </TestableTable>
    </TestableCard>
  </div>
);

BusinessViewLinkedTestsTable.propTypes = {
  tests: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      sourceTestId: PropTypes.string,
      name: PropTypes.string.isRequired,
      nameWithFnArea: PropTypes.string,
      description: PropTypes.string,
      failureDescriptionSource: PropTypes.string,
      testTypeId: PropTypes.number,
      testType: PropTypes.string,
      affectedArea: PropTypes.string,
      runAt: PropTypes.string,
      result: PropTypes.string.isRequired,
    })
  ),
  passingCount: PropTypes.number.isRequired,
  nonPassingCount: PropTypes.number.isRequired,
};

export default BusinessViewLinkedTestsTable;
